function cleanText(value) {
  return String(value ?? '').trim();
}

function cleanList(value) {
  const items = Array.isArray(value) ? value : cleanText(value).split(/[\n,]/);
  return [...new Set(items.map(cleanText).filter(Boolean))];
}

function nullableBoolean(value) {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
}

function catalogLookupKey(value) {
  return cleanText(value).toLocaleLowerCase('ko-KR');
}

export function getBossSelectionKey(boss = {}) {
  return cleanText(
    boss.id
    || boss.icon_id
    || boss.boss_key
    || boss.source_boss_id
    || boss.source_monster_model_id,
  );
}

export function normalizeBossMasterCatalogRow(row = {}) {
  const partCountText = cleanText(row.part_count);
  const raw = row.raw && typeof row.raw === 'object' && !Array.isArray(row.raw) ? row.raw : {};
  const importedNames = cleanList(raw.enikk_names);
  return {
    ...row,
    catalog_schema_ready: Object.prototype.hasOwnProperty.call(row, 'canonical_name'),
    id: cleanText(row.id),
    icon_id: cleanText(row.icon_id),
    canonical_name: cleanText(row.canonical_name || row.boss_name),
    source_boss_id: cleanText(row.source_boss_id),
    source_monster_model_id: cleanText(row.source_monster_model_id || row.monster_model_id),
    boss_image_url: cleanText(row.boss_image_url),
    display_image_url: cleanText(row.display_image_url || row.boss_image_url),
    display_image_source: cleanText(row.display_image_source || (row.boss_image_url ? 'boss_master' : '')),
    aliases: cleanList(row.aliases),
    has_core: nullableBoolean(row.has_core),
    core_pierceable: nullableBoolean(row.core_pierceable),
    has_parts: nullableBoolean(row.has_parts),
    part_count: partCountText && Number.isInteger(Number(partCountText)) && Number(partCountText) >= 0
      ? Number(partCountText)
      : null,
    parts_pierceable: nullableBoolean(row.parts_pierceable),
    mechanic_tags: cleanList(row.mechanic_tags),
    notes: cleanText(row.notes),
    is_verified: row.is_verified === true,
    verified_at: cleanText(row.verified_at),
    fact_source: row.is_verified === true ? '관리자 검증' : cleanText(raw.source) || '자동 수집',
    imported_names: importedNames.length ? importedNames : cleanList(row.aliases).filter((name) => /[A-Za-z]/.test(name)),
  };
}

export function attachBossMasterDisplayImages(masterRows = [], legacyRows = []) {
  const legacyLookup = new Map();
  for (const row of legacyRows) {
    const imageUrl = cleanText(row?.boss_image_url);
    if (!imageUrl) continue;
    for (const key of [catalogLookupKey(row?.boss_key), catalogLookupKey(row?.boss_name)]) {
      if (key && !legacyLookup.has(key)) legacyLookup.set(key, imageUrl);
    }
  }

  return masterRows.map((input) => {
    const row = normalizeBossMasterCatalogRow(input);
    if (row.boss_image_url) return row;
    const fallbackUrl = [row.boss_key, row.boss_name, row.canonical_name]
      .map(catalogLookupKey)
      .filter(Boolean)
      .map((key) => legacyLookup.get(key))
      .find(Boolean) || '';
    return {
      ...row,
      display_image_url: fallbackUrl,
      display_image_source: fallbackUrl ? 'boss_image_db' : '',
    };
  });
}

export function createBossMasterCatalogDraft(row = {}) {
  const normalized = normalizeBossMasterCatalogRow(row);
  return {
    ...normalized,
    aliasesText: normalized.aliases.join(', '),
    mechanicTagsText: normalized.mechanic_tags.join(', '),
    has_core: normalized.has_core === null ? '' : String(normalized.has_core),
    core_pierceable: normalized.core_pierceable === null ? '' : String(normalized.core_pierceable),
    has_parts: normalized.has_parts === null ? '' : String(normalized.has_parts),
    part_count: normalized.part_count === null ? '' : String(normalized.part_count),
    parts_pierceable: normalized.parts_pierceable === null ? '' : String(normalized.parts_pierceable),
  };
}

export function buildBossMasterCatalogUpdate(draft = {}) {
  const canonicalName = cleanText(draft.canonical_name);
  if (!canonicalName) throw new Error('보스명을 입력해주세요.');
  const partCount = cleanText(draft.part_count);
  if (partCount && (!/^\d+$/.test(partCount) || Number(partCount) < 0)) {
    throw new Error('부위 수는 0 이상의 정수로 입력해주세요.');
  }
  const hasParts = nullableBoolean(draft.has_parts);
  return {
    canonical_name: canonicalName,
    source_boss_id: cleanText(draft.source_boss_id) || null,
    source_monster_model_id: cleanText(draft.source_monster_model_id) || null,
    boss_image_url: cleanText(draft.boss_image_url),
    image_status: cleanText(draft.boss_image_url) ? 'verified' : 'unresolved',
    aliases: cleanList(draft.aliasesText),
    has_core: nullableBoolean(draft.has_core),
    core_pierceable: nullableBoolean(draft.core_pierceable),
    has_parts: hasParts,
    part_count: hasParts === false || !partCount ? null : Number(partCount),
    parts_pierceable: nullableBoolean(draft.parts_pierceable),
    mechanic_tags: cleanList(draft.mechanicTagsText),
    notes: cleanText(draft.notes),
    is_verified: Boolean(draft.is_verified),
  };
}

export function bossMasterTriStateLabel(value) {
  if (value === true) return '있음';
  if (value === false) return '없음';
  return '미확인';
}
